import type { ObjectToEntries } from "../lib/utils";
import type { ParameterRecord, ParamType } from "./params.types";

/**
 *
 */
export const paramTypes = ["search", "hash"] as const satisfies readonly ParamType[];

/**
 *
 *
 *
 *
 *
 */
export const charPlaceholders = [["+", "__PLUS__"]] as const;

export const toPlaceholders = (str: string): string => {
  for (const [char, placeholder] of charPlaceholders) {
    /*                                         */
    str = str.replaceAll(char, placeholder);
  }
  return str;
};

export const fromPlaceholders = <P extends string>(str: string): P => {
  for (const [char, placeholder] of charPlaceholders) {
    /*                                         */
    str = str.replaceAll(placeholder, char);
  }
  return str as P;
};

/**
 *
 *
 */
export const typeDelimiterMap = {
  search: "?",
  hash: "#",
} as const satisfies Record<ParamType, string>;

export type ParamsCache = URLSearchParams;

/**
 *
 *
 *
 *
 *
 */
export const fromStringParams = (rawParams: string): ParamsCache => {
  /*                                                                                   */
  return new URLSearchParams(toPlaceholders(rawParams));
};

/**
 *
 *
 *
 *
 */
export const toStringParams = (cache: ParamsCache, base: Location): string => {
  const encoder = new URL(base.origin);

  /*                                                                                    */
  const parts: string[] = [];
  for (const [key, value] of cache) {
    parts.push(`${key}=${value}`);
  }

  /*                                                                      */
  /*                                             */
  encoder.search = parts.join("&");

  /*                                                                            */
  /*                                                       */
  return fromPlaceholders(encoder.search.slice(1));
};

export const toEntries = <P extends ParameterRecord>(cache: ParamsCache): ObjectToEntries<P> => {
  const res: [string, string][] = [];

  for (const [key, value] of cache) {
    res.push([key, fromPlaceholders(value)]);
  }

  return res as never;
};

export const appendEntries = <P extends ParameterRecord>(
  cache: ParamsCache,
  entries: ObjectToEntries<P>,
) => {
  for (const [key, value] of entries) {
    if (Array.isArray(value)) value.forEach((v) => cache.append(key as string, uncoerceValue(v)));
    else cache.append(key as string, uncoerceValue(value));
  }

  return cache;
};

export const coerceValue = <R>(value: string): R => {
  if (value === "undefined") return undefined as never;

  try {
    return JSON.parse(value) as never;
  } catch {
    return value as never;
  }
};

export const uncoerceValue = (value: unknown): string => {
  return typeof value === "string" ? value : JSON.stringify(value);
};

type ObjectReducer<R> = (
  acc: Record<PropertyKey, R>,
  [key, value]: [PropertyKey, R],
) => Record<PropertyKey, R>;

/**
 *
 *
 *
 *
 *
 *
 */
export const getObjectReducer: <R>(coercer?: (_: string) => R) => ObjectReducer<R> = (
  coercer = (_) => _ as never,
) => {
  return (acc, [key, value]) => {
    const coerced = coercer(value as string);

    /*                                                      */
    if (!(key in acc)) {
      acc[key] = coerced;
    }
    /*                                                             */
    else if (Array.isArray(acc[key])) {
      acc[key].push(coerced);
    }
    /*                                                                            */
    else {
      acc[key] = [acc[key], coerced] as never;
    }

    return acc;
  };
};

/**
 *
 *
 *
 *
 *
 *
 *
 */
export const isValidKey = (key: string, filter: string[]): boolean => {
  return filter.length === 0 || filter.includes(key);
};

export const mergeParamCache = (
  target: ParamsCache,
  source: ParamsCache,
  filters: string[],
  deleted: Set<string>,
): ParamsCache => {
  /*                                                                                                */
  for (const key of source.keys()) {
    /*                                                                                       */
    if (!isValidKey(key, filters)) continue;

    /*                                                                 */
    const [first, ...rest] = source.getAll(key);
    target.set(key, first);
    for (const value of rest) {
      target.append(key, value);
    }
  }

  /*                                                           */
  for (const filter of filters) {
    if (!source.has(filter)) target.delete(filter);
  }

  /*                                                */
  for (const deletedKey of deleted) {
    if (!source.has(deletedKey)) target.delete(deletedKey);
  }

  return target;
};

export const mergeWithCurrent = (
  res: ParamsCache,
  filters: string[],
  deleted: Set<string>,
  curUrl: URL,
  curParams: ParamsCache,
): readonly [URL, ParamsCache] => {
  return [curUrl, mergeParamCache(curParams, res, filters, deleted)] as const;
};
